import React, { useState, useEffect } from 'react';
import './UserExcelViewer.css';

interface Cell {
  value: string | number;
  format?: {
    bold?: boolean;
    italic?: boolean;
    backgroundColor?: string;
    color?: string;
    textAlign?: 'left' | 'center' | 'right';
  };
}

interface UserExcelViewerProps {
  task: {
    id: string;
    headers: string[];
    data: { [key: string]: any };
    sheetName: string;
    workbookId: string;
  };
  onClose: () => void;
}

const UserExcelViewer: React.FC<UserExcelViewerProps> = ({ task, onClose }) => {
  const [cellValue, setCellValue] = useState<string>('');
  const [editingCell, setEditingCell] = useState<{ row: number; col: number } | null>(null);
  const [selectedCell, setSelectedCell] = useState<{ row: number; col: number } | null>(null);

  // Create EXACT Excel data from admin - no modifications
  const createUserExcelData = () => {
    console.log('📊 Creating EXACT admin Excel data for user:', task);
    console.log('📊 Admin headers:', task.headers);
    console.log('📊 Admin data:', task.data);
    
    // Create header row with EXACT admin headers
    const headerRow: Cell[] = task.headers.map(header => ({
      value: header,
      format: { 
        bold: true, 
        backgroundColor: '#f0f0f0', 
        textAlign: 'center' as const 
      }
    }));
    
    // Create data row with EXACT admin data
    const dataRow: Cell[] = task.headers.map(header => {
      const value = String(task.data[header] || '');
      console.log(`📊 EXACT mapping: "${header}" = "${value}"`);
      
      return {
        value: value,
        format: {
          backgroundColor: undefined,
          color: value ? '#000000' : '#999999'
        }
      };
    });
    
    // Create empty rows for user to add more data
    const emptyRows: Cell[][] = [];
    for (let i = 0; i < 10; i++) {
      const row: Cell[] = task.headers.map(header => ({ 
        value: '', 
        format: {
          backgroundColor: undefined
        }
      }));
      emptyRows.push(row);
    }
    
    const sheetData = [headerRow, dataRow, ...emptyRows];
    
    console.log('📊 EXACT admin Excel data created for user:', {
      headers: headerRow.map(cell => cell.value),
      dataRow: dataRow.map(cell => cell.value),
      totalRows: sheetData.length,
      exactDataMapping: task.headers.map((header, index) => ({
        header,
        value: dataRow[index]?.value,
        adminData: task.data[header],
        isExact: dataRow[index]?.value === task.data[header]
      }))
    });
    
    return sheetData;
  };

  const [sheetData, setSheetData] = useState<Cell[][]>([]);

  useEffect(() => {
    const data = createUserExcelData();
    setSheetData(data);
  }, [task]);

  const handleCellClick = (row: number, col: number) => {
    console.log(`🖱️ Cell clicked: [${row}, ${col}]`);
    setSelectedCell({ row, col });
    
    const cell = sheetData[row]?.[col];
    if (cell) {
      setCellValue(String(cell.value || ''));
      setEditingCell({ row, col });
    }
  };

  const handleCellDoubleClick = (row: number, col: number) => {
    console.log(`🖱️ Cell double-clicked: [${row}, ${col}]`);
    setSelectedCell({ row, col });
    setEditingCell({ row, col });
    
    const cell = sheetData[row]?.[col];
    if (cell) {
      setCellValue(String(cell.value || ''));
    }
  };

  const handleCellKeyDown = async (event: React.KeyboardEvent, row: number, col: number) => {
    const { key } = event;
    
    switch (key) {
      case 'Enter':
        event.preventDefault();
        if (editingCell) {
          await saveCellValue(row, col);
          moveToNextRow(row, col);
        } else {
          setEditingCell({ row, col });
        }
        break;
      case 'Tab':
        event.preventDefault();
        if (editingCell) {
          await saveCellValue(row, col);
        }
        moveToNextCol(row, col);
        break;
      case 'Escape':
        event.preventDefault();
        setEditingCell(null);
        setCellValue('');
        break;
    }
  };

  const saveCellValue = async (row: number, col: number) => {
    const value = cellValue.trim();
    
    console.log(`💾 Saving cell [${row}, ${col}] with value: "${value}"`);
    
    const newSheetData = [...sheetData];
    if (!newSheetData[row]) {
      newSheetData[row] = [];
    }
    
    while (newSheetData[row].length <= col) {
      newSheetData[row].push({ value: '' });
    }
    
    newSheetData[row][col] = {
      value: value,
      format: {
        backgroundColor: col === 1 ? '#e3f2fd' : undefined, // Concerned Person column
        color: value ? '#000000' : '#999999'
      }
    };
    
    setSheetData(newSheetData);
    setEditingCell(null);
    
    console.log(`✅ Cell saved: [${row}, ${col}] = "${value}"`);
  };

  const moveToNextRow = (row: number, col: number) => {
    const nextRow = Math.min(row + 1, sheetData.length - 1);
    setSelectedCell({ row: nextRow, col });
  };

  const moveToNextCol = (row: number, col: number) => {
    const nextCol = Math.min(col + 1, task.headers.length - 1);
    setSelectedCell({ row, col: nextCol });
  };

  const handleFormulaBarChange = (value: string) => {
    setCellValue(value);
    if (selectedCell) {
      console.log(`📝 Formula bar updated for cell [${selectedCell.row}, ${selectedCell.col}]`);
    }
  };

  const handleFormulaBarKeyDown = async (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && selectedCell) {
      event.preventDefault();
      await saveCellValue(selectedCell.row, selectedCell.col);
      setEditingCell(selectedCell);
    } else if (event.key === 'Escape' && selectedCell) {
      event.preventDefault();
      const cell = sheetData[selectedCell.row][selectedCell.col];
      setCellValue(String(cell.value || ''));
    }
  };

  const COLUMN_HEADERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];

  return (
    <div className="user-excel-viewer">
      <div className="excel-header">
        <div className="header-left">
          <h2>📊 {task.sheetName} - Your Assigned Tasks</h2>
          <p>View and edit your assigned tasks</p>
        </div>
        <div className="header-right">
          <button onClick={onClose} className="btn-close">
            ← Back to Tasks
          </button>
        </div>
      </div>

      <div className="excel-container">
        {/* Formula Bar */}
        <div className="formula-bar">
          <div className="cell-reference">
            {selectedCell ? `${COLUMN_HEADERS[selectedCell.col]}${selectedCell.row + 1}` : 'A1'}
          </div>
          <input
            ref={(input) => {
              if (input) {
                input.focus();
              }
            }}
            type="text"
            value={cellValue}
            onChange={(e) => handleFormulaBarChange(e.target.value)}
            onKeyDown={handleFormulaBarKeyDown}
            placeholder="Enter formula or value"
            className="formula-input"
          />
        </div>

        {/* Excel Grid */}
        <div className="excel-grid">
          {/* Column Headers */}
          <div className="excel-row header-row">
            <div className="excel-cell row-header"></div>
            {task.headers.map((header, colIndex) => (
              <div 
                key={colIndex} 
                className="excel-cell column-header"
                style={{ 
                  width: 150,
                  backgroundColor: header === 'Concerned Person' ? '#e3f2fd' : '#f0f0f0',
                  fontWeight: 'bold',
                  textAlign: 'center'
                }}
              >
                {header}
              </div>
            ))}
          </div>

          {/* Data Rows */}
          {sheetData.slice(1).map((row, rowIndex) => (
            <div key={rowIndex} className="excel-row">
              <div className="excel-cell row-header">
                {rowIndex + 1}
              </div>
              {row.map((cell, colIndex) => (
                <div
                  key={colIndex}
                  className={`excel-cell data-cell ${
                    selectedCell?.row === (rowIndex + 1) && selectedCell?.col === colIndex ? 'selected' : ''
                  } ${editingCell?.row === (rowIndex + 1) && editingCell?.col === colIndex ? 'editing' : ''}`}
                  onClick={() => handleCellClick(rowIndex + 1, colIndex)}
                  onDoubleClick={() => handleCellDoubleClick(rowIndex + 1, colIndex)}
                  onKeyDown={(e) => handleCellKeyDown(e, rowIndex + 1, colIndex)}
                  tabIndex={0}
                  style={{
                    width: 150,
                    height: 32,
                    fontWeight: cell.format?.bold ? 'bold' : 'normal',
                    fontStyle: cell.format?.italic ? 'italic' : 'normal',
                    color: cell.format?.color || 'inherit',
                    backgroundColor: cell.format?.backgroundColor || 'inherit',
                    textAlign: cell.format?.textAlign || 'left'
                  }}
                >
                  {editingCell?.row === (rowIndex + 1) && editingCell?.col === colIndex ? (
                    <input
                      type="text"
                      value={cellValue}
                      onChange={(e) => setCellValue(e.target.value)}
                      onBlur={async () => {
                        await saveCellValue(rowIndex + 1, colIndex);
                        setEditingCell(null);
                      }}
                      onKeyDown={async (e) => {
                        if (e.key === 'Enter' || e.key === 'Tab') {
                          e.preventDefault();
                          await saveCellValue(rowIndex + 1, colIndex);
                          setEditingCell(null);
                          if (e.key === 'Enter') moveToNextRow(rowIndex + 1, colIndex);
                          else moveToNextCol(rowIndex + 1, colIndex);
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          setEditingCell(null);
                          setCellValue(String(cell.value || ''));
                        }
                      }}
                      autoFocus
                      className="cell-input"
                      style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        outline: 'none',
                        background: 'white',
                        fontSize: 'inherit',
                        fontFamily: 'inherit',
                        padding: '2px',
                        margin: '0',
                        boxShadow: '0 0 0 2px #3b82f6'
                      }}
                    />
                  ) : (
                    String(cell.value || '')
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UserExcelViewer;
